import { useState } from 'react';
import type { Automation } from '@capture/shared';
import { FormErrorInline } from '@/components/FormErrorInline';
import { SectionCard } from './SectionCard';
import styles from './panel.module.css';

interface Props {
  automation: Automation;
  onChange: (next: Automation) => void;
}

function identitySummary(automation: Automation): string {
  return automation.name.trim() || 'Untitled automation';
}

/**
 * Name/description/active toggle. Name is required — the panel page gates
 * `save()` on it, and this section surfaces the block inline so the operator
 * sees why nothing persisted.
 *
 * Name and description keep a LOCAL draft while the operator types and only
 * commit upward (`onChange`) on blur — Enter also commits the name. Committing
 * every keystroke used to flow straight into `usePanelAutomation.save()`,
 * which compiles + `replaceAutomationRules`s on every character (an O(N)
 * `assembleAutomations` pass over the whole form per keystroke). The Active
 * toggle is cheap and still commits immediately. Callers that can swap to a
 * *different* automation without unmounting this component (e.g.
 * `AutomationPanelPage`) MUST key it on the automation's identity so the
 * local draft re-seeds instead of leaking the previous automation's text.
 */
export function IdentitySection({ automation, onChange }: Props) {
  const [localName, setLocalName] = useState(automation.name);
  const [localDescription, setLocalDescription] = useState(automation.description ?? '');
  const nameMissing = !automation.name.trim();

  function commitName() {
    if (localName !== automation.name) onChange({ ...automation, name: localName });
  }

  function commitDescription() {
    if (localDescription !== (automation.description ?? '')) {
      onChange({ ...automation, description: localDescription });
    }
  }

  return (
    <div data-testid="identity-section">
      <SectionCard title="Identity" summary={identitySummary(automation)} defaultOpen>
        <div className={styles.formGrid}>
          <label className={styles.fieldLabel} htmlFor="automation-name">
            Name
          </label>
          <input
            id="automation-name"
            className={styles.textInput}
            data-testid="automation-name-input"
            type="text"
            value={localName}
            onChange={(e) => setLocalName(e.target.value)}
            onBlur={commitName}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitName();
            }}
          />

          <label className={styles.fieldLabel} htmlFor="automation-description">
            Description
          </label>
          <textarea
            id="automation-description"
            className={styles.textInput}
            data-testid="automation-description-input"
            value={localDescription}
            onChange={(e) => setLocalDescription(e.target.value)}
            onBlur={commitDescription}
            rows={2}
          />

          <span className={styles.fieldLabel}>Active</span>
          <label className={styles.checkboxLabel}>
            <input
              type="checkbox"
              data-testid="automation-active-toggle"
              checked={automation.isActive}
              onChange={(e) => onChange({ ...automation, isActive: e.target.checked })}
            />
            Active
          </label>
        </div>
        {nameMissing && (
          <FormErrorInline error={new Error('Name is required — enter a name to save.')} />
        )}
      </SectionCard>
    </div>
  );
}
